// Las dos salidas de un catálogo con fotos (ADR-2026-10-15-el-catalogo-se-descarga-y-se-lanza):
// "Descargar" baja todas en UN zip —el de "descargar todo" del chat, con la misma pregunta de
// marca de agua y formato que el resto de la app— y "Publicar en tu tienda online" las manda a un
// lanzamiento, sólo si la empresa tiene los lanzamientos activados. Sin fotos no se muestra nada.
import { Download, Loader2 } from 'lucide-react';
import { useMemo, useState } from 'react';
import EnviarALanzamiento from '../../../../components/EnviarALanzamiento';
import { useOpcionesDeDescarga } from '../../../../components/descarga/useOpcionesDeDescarga';
import { useTiendaConectada } from '../../../../hooks/useTiendaConectada';
import { descargarComoZip, type ArchivoDeLote } from '../../../../utils/descargarLote';
import { firmarOriginales } from './generarCatalogo';
import type { PlanDeCatalogo } from './plan';
import { fotosDelCatalogo, nombreDelZip, paraLanzamiento } from './fotosDelCatalogo';

/** El botón secundario del pie, como "Modificar catálogo". */
const BOTON =
  'inline-flex items-center gap-2 rounded-xl border border-black/[0.1] bg-white py-3 px-5 text-[15px] font-semibold text-brand-black transition-colors hover:bg-black/[0.03] disabled:cursor-not-allowed disabled:opacity-50';
/** En el teléfono, sólo el ícono: la barra de abajo es de la pastilla del agente
 *  (ADR-2026-10-15-el-catalogo-en-el-telefono). */
const BOTON_ICONO =
  'grid h-12 w-12 shrink-0 place-items-center rounded-full bg-white text-brand-black shadow-[0_8px_24px_-10px_rgb(var(--brand-black-rgb)_/_0.28),0_1px_3px_rgb(var(--brand-black-rgb)_/_0.08)] ring-1 ring-black/[0.06] transition-transform active:scale-95 disabled:opacity-50';

export const SalidasDelCatalogo: React.FC<{
  plan: PlanDeCatalogo;
  companyId: string | null;
  brandId: string | null;
  /** Botones redondos sin texto (el teléfono). Default false: el pie de escritorio de siempre. */
  soloIconos?: boolean;
}> = ({ plan, companyId, brandId, soloIconos = false }) => {
  const fotos = useMemo(() => fotosDelCatalogo(plan), [plan]);
  const tiendaConectada = useTiendaConectada();
  const { chooserElement, resolverOpciones, aplicarOpciones } = useOpcionesDeDescarga();
  const [progreso, setProgreso] = useState<{ hechos: number; total: number } | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  if (fotos.length === 0) return null;

  const descargar = async () => {
    if (progreso || !companyId) return;
    setAviso(null);
    try {
      const eleccion = await resolverOpciones({ cantidad: fotos.length });
      if (eleccion === null) return;
      setProgreso({ hechos: 0, total: fotos.length });
      const firmadas = await firmarOriginales(
        fotos.map((f) => f.imageId),
        companyId,
      );
      const archivos: ArchivoDeLote[] = fotos.flatMap((f) => {
        const url = firmadas.get(f.imageId);
        return url ? [{ url, nombre: f.nombre, imagenId: f.imageId }] : [];
      });
      const r = await descargarComoZip(
        archivos,
        nombreDelZip(plan.titulo),
        (hechos, total) => setProgreso({ hechos, total }),
        (blob) => aplicarOpciones(blob, false, eleccion),
      );
      const fallidas = r.fallidos + (fotos.length - archivos.length);
      if (r.ok === 0) setAviso('No se pudo descargar ninguna. Probá de nuevo.');
      else if (fallidas > 0) setAviso(`Bajé ${r.ok}; ${fallidas} no se pudieron.`);
    } catch (e) {
      setAviso(e instanceof Error ? e.message : 'No se pudo descargar.');
    } finally {
      setProgreso(null);
    }
  };

  return (
    <>
      {aviso &&
        (soloIconos ? (
          <span role="status" className="absolute bottom-full right-3 mb-2 max-w-[80%] truncate rounded-full bg-brand-black py-1.5 px-3 text-[12.5px] font-medium text-white shadow-lg">
            {aviso}
          </span>
        ) : (
          <span className="max-w-[220px] truncate text-[13px] text-[#B42318]" title={aviso}>
            {aviso}
          </span>
        ))}
      <button
        type="button"
        onClick={() => void descargar()}
        disabled={!!progreso}
        className={soloIconos ? BOTON_ICONO : BOTON}
        title={`Descargar las ${fotos.length} fotos en un zip`}
        aria-label={soloIconos ? `Descargar las ${fotos.length} fotos en un zip` : undefined}
      >
        {progreso ? <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> : <Download className={soloIconos ? 'h-5 w-5' : 'h-4 w-4'} strokeWidth={2} aria-hidden="true" />}
        {!soloIconos && (progreso ? `${progreso.hechos}/${progreso.total}` : 'Descargar')}
      </button>
      {tiendaConectada && (
        <EnviarALanzamiento
          companyId={companyId}
          brandId={brandId}
          imagenes={paraLanzamiento(fotos)}
          direccion="arriba"
          alineacion="derecha"
          contenedorClassName="shrink-0"
          tamanoIcono={soloIconos ? 'h-5 w-5' : 'h-4 w-4'}
          label="Lanzamiento"
          labelClassName={soloIconos ? 'sr-only' : ''}
          cadaFotoASuProducto
          className={soloIconos ? BOTON_ICONO : BOTON}
        />
      )}
      {chooserElement}
    </>
  );
};
